import {
  Component,
  DestroyRef,
  effect,
  inject,
  OnDestroy,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core'
import { Router, ActivatedRoute, NavigationEnd } from '@angular/router'
import { filter } from 'rxjs'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { ProductMovesListComponent } from '../../components/product-components/product-moves-list/product-moves-list.component'
import { Stock } from '../../models'
import { CoreService, NotificationService, TitleService } from '../../services'
import { TranslateService } from '@ngx-translate/core'
import { Product, ProductPrice } from 'depoto-core/src/entities'
import { ProductPriceLevelsComponent } from '../../components/product-components/product-price-levels/product-price-levels.component'
import { SchemaUtil, historyBack, getObjectDiff, isForbiddenError } from '../../utils'
import { BaseDetail } from '../_base-detail/base-detail'
import { ProductDetailService } from '../../product-detail.service'
import { ProductFormComponent } from '../../components/product-components/product-form/product-form.component'
import { injectQueryClient } from '@tanstack/angular-query-experimental'

@Component({
  selector: 'app-product',
  templateUrl: './product.component.html',
  styleUrls: ['product.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ProductComponent extends BaseDetail implements OnInit, OnDestroy {
  @ViewChild('productForm') productForm: ProductFormComponent
  @ViewChild('movesList') movesList: ProductMovesListComponent
  @ViewChild('productPriceLevelsEl') productPriceLevelsEl: ProductPriceLevelsComponent

  private queryClient = injectQueryClient()
  private readonly destroyRef = inject(DestroyRef)

  activeLink = ''
  activeLinks: string[] = [
    'common',
    'stock',
    'moves',
    'images',
    'files',
    'parameters',
    'categories',
    'packing',
    'advancedPrices',
    'priceLevels',
    'print',
    'leaflets',
  ]

  canMoveProducts = false

  private pristineProduct: Product

  constructor(
    public route: ActivatedRoute,
    public router: Router,
    public core: CoreService,
    public productDetailService: ProductDetailService,
    public translateService: TranslateService,
    private notificationService: NotificationService,
    private titleService: TitleService
  ) {
    // todo ??
    super(route, router, core, translateService)
    effect(() => {
      this.productDetailService.queryCarriers.data()
      this.productDetailService.queryCurrencies.data()
      this.productDetailService.queryPayments.data()
      this.productDetailService.queryProducers.data()
      this.productDetailService.queryDepotsOptions.data()
      this.productDetailService.queryVats.data()
      this.productDetailService.querySuppliers.data()
    })
  }

  async ngOnInit() {
    this.route.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      if (params?.id > 0) {
        this.productDetailService.isCreating = true
        this.productDetailService.isDuplicatedProduct = true
        this.refreshData(params.id)
      }
    })

    this.router.events
      .pipe(
        filter(val => val instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.setActiveLink()
      })

    this.productDetailService.refreshDataEvent$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      void this.reloadProduct()
    })

    this.productDetailService.resolveStockEvent$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.resolveStockFromCurrentProduct()
    })

    this.watchAccountSwitch(() => {
      if (
        !this.productDetailService.isCreating &&
        this.productDetailService.product?.id &&
        !this.hasUnsavedDataChanges()
      ) {
        this.refreshData(this.productDetailService.product.id)
      }
    })

    this.setActiveLink()
    this.productDetailService.getRoles()
    if (!this.productDetailService.isDuplicatedProduct) await this.reloadViewDataFromRoute()
    this.canMoveProducts = this.hasRoleForMoveProducts()
    // this.productDetailService.getCoreEntityLists()

    this.pristineProduct = structuredClone(this.productDetailService.product)
  }

  async ngOnDestroy() {
    this.unwatchAccountSwitch()
    // Invalidate any in-flight fetch this instance kicked off (e.g. refreshData's network call)
    // so that if it resolves after we're gone, it recognizes it's stale and no-ops instead of
    // clobbering whatever product/isCreating state the next page has already set.
    this.productDetailService.loadToken++
    this.productDetailService.isCreating = false
    this.productDetailService.isDuplicatedProduct = false
    this.productDetailService.product = undefined
  }

  hasUnsavedDataChanges(): boolean {
    const currentProduct = this.buildComparableProductSnapshot(this.productDetailService.product)
    const pristineProduct = this.buildComparableProductSnapshot(this.pristineProduct)
    const diff = getObjectDiff(currentProduct, pristineProduct)
    // the func above returns different properties' names if the objects aren't equal
    // If there are "tags" among them, take into account that every tag is an object and even if names are the same,
    // the id and other properties may not correspond.
    if (!diff) {
      return false
    }
    return !!diff.filter(d => d !== 'files').length
  }

  private buildComparableProductSnapshot(product: Product): Product {
    const productSnapshot = structuredClone(product)

    // The user can't change child productDepots from this page.
    productSnapshot?.bundleChildren?.forEach(bundleChild => {
      delete bundleChild.child?.productDepots
    })
    productSnapshot?.children?.forEach(child => {
      delete child.productDepots
    })

    return productSnapshot
  }

  goBack(): void {
    historyBack()
  }

  async setActiveLink() {
    const urlParts = window.location.href.split('/')
    if (this.activeLink === 'common') {
      if (
        !!this.productDetailService.product?.id &&
        urlParts[urlParts.length - 2] === 'product' &&
        Number(this.router.url.split('/')[this.router.url.split('/').length - 1]) !==
          this.productDetailService.product?.id
      ) {
        await this.reloadViewDataFromRoute()
      }
    } else {
      if (
        !!this.productDetailService.product?.id &&
        urlParts[urlParts.length - 3] === 'product' &&
        Number(this.router.url.split('/')[this.router.url.split('/').length - 2]) !==
          this.productDetailService.product?.id
      ) {
        await this.reloadViewDataFromRoute()
      }
    }

    if (this.productDetailService.isCreating) {
      if (this.router.url.split('/')[3] === undefined) {
        this.activeLink = 'common'
        this.productDetailService.product = new Product({ enabled: true })
      } else {
        this.activeLink = String(this.router.url.split('/')[3])
      }
    } else if (urlParts[urlParts.length - 2] === 'product-variant-create') {
      this.activeLink = 'common'
      this.productDetailService.isCreating = true
      this.productDetailService.product = new Product({ enabled: true })
    } else {
      if (this.router.url.split('/')[3] === undefined) this.activeLink = 'common'
      else this.activeLink = String(this.router.url.split('/')[this.router.url.split('/').length - 1])
    }
  }

  private async reloadViewDataFromRoute() {
    // todo hotfix - why this happened -> this.route.snapshot.params['id'] took old cached id, even last send param was right id
    // const id = this.route.snapshot.params['id']
    let id
    if (this.activeLink === 'common') {
      id = Number(this.router.url.split('/')[this.router.url.split('/').length - 1])
    } else {
      id = Number(this.router.url.split('/')[this.router.url.split('/').length - 2])
    }
    // Claims this load as the current one. If a newer load starts (or this component is
    // destroyed) before our async work below resolves, loadToken will have moved on and we know
    // to discard our result instead of clobbering whatever is current by then.
    const token = ++this.productDetailService.loadToken
    if (isNaN(id)) {
      this.productDetailService.isCreating = true
      this.productDetailService.product = new Product({ enabled: true })
      this.productDetailService.loading = false
      await this.refreshData(undefined, token)
      if (token !== this.productDetailService.loadToken) return
      this.pristineProduct = structuredClone(this.productDetailService.product)
      return
    }
    if (this.route.snapshot.routeConfig.path.includes('product-variant-create') && !isNaN(id)) {
      this.productDetailService.isCreating = true
      this.productDetailService.product = new Product({ enabled: true })
      this.productDetailService.product.vat = this.productDetailService.queryVats.data().find(vat => vat.default)
      const parent = await this.core.services.product.getById(id, SchemaUtil.product)
      if (token !== this.productDetailService.loadToken) return
      this.productDetailService.product.parent = parent
      await this.refreshData(undefined, token)
    } else {
      this.productDetailService.isCreating = false
      await this.refreshData(id, token)
    }
    if (token !== this.productDetailService.loadToken) return
    this.pristineProduct = structuredClone(this.productDetailService.product)
  }

  public async refreshData(id?: number, token?: number): Promise<any> {
    // Callers that didn't come through reloadViewDataFromRoute() (e.g. the duplicate-product
    // queryParams subscription, or the refreshDataEvent$ subscriber) still need a token so their
    // async fetch below can be recognized as stale if superseded.
    const requestToken = token ?? ++this.productDetailService.loadToken
    this.productDetailService.loading = true
    // this.getCoreEntityLists()
    if (
      this.productDetailService.isCreating &&
      !this.productDetailService.product &&
      !this.productDetailService.isDuplicatedProduct
    ) {
      this.productDetailService.product = new Product({ enabled: true })
      this.activeLink = 'common'
    }
    if (this.productDetailService.product && !this.productDetailService.product.vat) {
      this.productDetailService.product.vat = this.productDetailService.queryVats.data().find(vat => vat.default)
    }
    if (!this.productDetailService.isCreating || this.productDetailService.isDuplicatedProduct) {
      let fetchedProduct: Product
      try {
        const schema = this.productDetailService.isDuplicatedProduct ? SchemaUtil.duplicatedProduct : SchemaUtil.product
        fetchedProduct = await this.core.services.product.getByIdOrThrow(id, schema)
      } catch (e) {
        if (requestToken !== this.productDetailService.loadToken) return
        console.warn(e)
        if (isForbiddenError(e)) {
          return this.onLoadError(403, { forbiddenRedirectRouteTo: ['/products'] })
        }
        return this.onLoadError(404)
      }
      if (requestToken !== this.productDetailService.loadToken) {
        // A newer navigation/load has started (or this instance was destroyed) since this fetch
        // began; discard the now-stale result instead of overwriting current state.
        return
      }
      this.productDetailService.product = fetchedProduct
      // Update title with product name
      if (this.productDetailService.product) {
        this.titleService.updateTitleWithObject(
          this.productDetailService.product.id,
          this.productDetailService.product.name,
          'title.product-id'
        )
      }
      if (!this.productDetailService.product) {
        return this.onLoadError(404)
      }
      this.productDetailService.product.advancedPrices = [...this.productDetailService.product.advancedPrices].map(
        advancedPrice =>
          new ProductPrice({
            ...advancedPrice,
            dateFrom: this.formatDateStringFromDateTime(advancedPrice.dateFrom),
            dateTo: this.formatDateStringFromDateTime(advancedPrice.dateTo),
          })
      )
      if (this.productDetailService.isDuplicatedProduct) {
        delete this.productDetailService.product.id
        if (this.productDetailService.product.bundleChildren?.length) {
          this.productDetailService.product.bundleChildren.forEach((a, i) => {
            delete this.productDetailService.product.bundleChildren[i].id
          })
        }
      } else {
        this.formatStock()
      }
      if (this.movesList) {
        this.movesList.queryProductMoves.refetch()
      }
    }
    this.productDetailService.loading = false
  }

  getCoreEntityLists(): void {
    // todo
    // this.productDetailService.querySuppliers.data() = this.core.lists.suppliers
    // this.productDetailService.vats = this.core.lists.vats
    // this.productDetailService.producers = this.core.lists.producers
    // this.productDetailService.queryCurrencies.data() = this.core.lists.currencies
  }

  public resolveStockFromCurrentProduct(): void {
    this.formatStock()
    if (this.movesList) {
      this.movesList.queryProductMoves.refetch()
    }
    this.pristineProduct = structuredClone(this.productDetailService.product)
  }

  public async reloadProduct(): Promise<void> {
    await this.refreshData(this.productDetailService.product.id)
    this.pristineProduct = structuredClone(this.productDetailService.product)
  }

  private formatStock(): void {
    this.productDetailService.loading = true
    this.productDetailService.stock = []
    this.productDetailService.stockDepotsChartData = []
    const depots = (this.productDetailService.queryDepotsOptions.data() as any[]) ?? []
    depots.forEach(depot => {
      const quantity = this.productDetailService.product.quantities.find(q => q.depot.id === depot.id)
      this.productDetailService.stock.push({
        depot: depot as any,
        productDepots: [] as any,
        sumAll: quantity?.quantityStock || 0,
        sumReservations: quantity?.quantityReservation || 0,
        sumAvailable: quantity?.quantityAvailable || 0,
        sumPurchase: quantity?.quantityPurchase || 0,
      })
    })

    if (!this.productDetailService.product.isBundle) {
      this.productDetailService.product.productDepots.forEach(pDepot => {
        this.productDetailService.stock.forEach((stockItem, index) => {
          if (pDepot.depot.id === stockItem.depot.id) {
            stockItem.productDepots.push(pDepot as any)
            // this.productDetailService.stock[index].sumAll += pDepot.quantityStock
            // this.productDetailService.stock[index].sumReservations += pDepot.quantityReservation
            // this.productDetailService.stock[index].sumAvailable =
            //   this.productDetailService.stock[index].sumAll - this.productDetailService.stock[index].sumReservations >= 0
            //     ? this.productDetailService.stock[index].sumAll - this.productDetailService.stock[index].sumReservations
            //     : 0
            // this.productDetailService.product.quantities.find(q => q.depot.id === pDepot.depot)
            // this.productDetailService.stock[index].sumPurchase = pDepot.product.quantityPurchase
          }
        })
      })
      try {
        const itemsCount = this.productDetailService.stock.map(d => d.productDepots.length).reduce((a, b) => a + b)
        this.productDetailService.isStockEmptyYet = itemsCount === 0
      } catch (e) {
        // Reduce of empty array with no initial value
        this.productDetailService.isStockEmptyYet = true
      }
      this.getLastStockItemData()
    }

    this.productDetailService.totalQuantityStock = this.productDetailService.stock.reduce(
      (total, stockItem) => stockItem.sumAll + total,
      0
    )
    this.productDetailService.totalQuantityReservation = this.productDetailService.stock.reduce(
      (total, stockItem) => stockItem.sumReservations + total,
      0
    )
    this.productDetailService.totalQuantityAvailable = this.productDetailService.stock.reduce(
      (total, stockItem) => stockItem.sumAvailable + total,
      0
    )
    this.productDetailService.totalQuantityPurchase = this.productDetailService.stock.reduce(
      (total, stockItem) => stockItem.sumPurchase + total,
      0
    )
    this.productDetailService.loading = false
  }

  private getLastStockItemData(): void {
    this.productDetailService.stock.forEach(stock => {
      if (stock.productDepots.length > 0) {
        // @ts-ignore
        stock.productDepots.sort((a, b) => (a.id === b.id ? 0 : a.id < b.id))
      }
    })
    this.productDetailService.stock.sort(this.sortStock)
    if (this.productDetailService.stock[0] && this.productDetailService.stock[0].productDepots[0]) {
      this.core.services.storage.set(
        this.getStorageKeyForEntity('lastStockItemSupplier'),
        this.productDetailService.stock[0].productDepots[0].supplier.id
      )
      this.core.services.storage.set(
        this.getStorageKeyForEntity('lastStockItemDepot'),
        this.productDetailService.stock[0].productDepots[0].depot.id
      )
      /* this.core.services.storage.set(
        this.getStorageKeyForEntity('lastStockItemPurchasePrice'),
        this.productDetailService.stock[0].productDepots[0].purchasePrice
      ) never read */
    }
  }

  private sortStock(a: Stock, b: Stock) {
    if (a.productDepots[0] && b.productDepots[0]) {
      if (a.productDepots[0].id === b.productDepots[0].id) {
        return 0
      } else {
        if (a.productDepots[0].id < b.productDepots[0].id) {
          return 1
        } else {
          return -1
        }
      }
    } else {
      return 0
    }
  }

  public async createProduct(): Promise<void> {
    this.productDetailService.submitted = true
    if (
      this.productDetailService.product?.name?.length < 1 ||
      this.productDetailService.product?.sellPrice < 0 ||
      (!this.productDetailService.product?.sellPrice && this.productDetailService.product?.sellPrice !== 0)
    ) {
      if (this.productDetailService.product?.name?.length < 1 && !this.productDetailService.product?.sellPrice) {
        alert(this.translateService.instant('product.enter.name.and.sellprice'))
      } else if (this.productDetailService.product?.name?.length < 1) {
        alert(this.translateService.instant('product.enter.name'))
      } else if (!this.productDetailService.product?.sellPrice) {
        alert(this.translateService.instant('product.enter.sellprice'))
      }
      this.productDetailService.submitted = false
      return
    }

    if (!this.productDetailService.product?.vat?.id) {
      const defaultVat = this.productDetailService.queryVats.data()?.find(vat => vat.default)
      if (!defaultVat?.id) {
        this.notificationService.error(this.translateService.instant('vat.not-loaded'))
        this.productDetailService.submitted = false
        return
      }
      this.productDetailService.product.vat = defaultVat
    }

    try {
      this.productDetailService.loading = true
      const result = await this.core.services.product.create(this.productDetailService.product, SchemaUtil.product)
      if (!result?.id) {
        this.productDetailService.submitted = false
        return
      }

      this.notificationService.success(this.translateService.instant('alert.product.added'))
      if (this.productDetailService.product.bundleChildren?.length) {
        for (const bc of this.productDetailService.product.bundleChildren) {
          bc.parent = new Product(result)
          await this.core.services.productBundle.create(bc, SchemaUtil.productBundle)
        }
      }
      this.productDetailService.isCreating = false
      this.pristineProduct = structuredClone(this.productDetailService.product)
      this.router.navigate(['/product', result.id], { replaceUrl: true })
    } catch (e) {
      console.error(e)
    } finally {
      this.productDetailService.loading = false
    }
  }

  public async updateProduct(): Promise<void> {
    this.productDetailService.submitted = true
    if (
      this.productDetailService.product?.name?.length < 1 ||
      this.productDetailService.product?.sellPrice < 0 ||
      (!this.productDetailService.product?.sellPrice && this.productDetailService.product?.sellPrice !== 0)
    ) {
      if (this.productDetailService.product?.name?.length < 1 && !this.productDetailService.product?.sellPrice) {
        alert(this.translateService.instant('product.enter.name.and.sellprice'))
      } else if (this.productDetailService.product?.name?.length < 1) {
        alert(this.translateService.instant('product.enter.name'))
      } else if (!this.productDetailService.product?.sellPrice) {
        alert(this.translateService.instant('product.enter.sellprice'))
      }
      this.productDetailService.submitted = false
      return
    }
    this.productDetailService.loading = true
    try {
      if (this.productDetailService.updatedProductPriceLevels?.length) {
        for (const updatedProductPriceLevel of this.productDetailService.updatedProductPriceLevels) {
          const exists = this.productDetailService.product.productPriceLevels.some(
            pPL => pPL.priceLevel.id === updatedProductPriceLevel.priceLevel.id
          )
          if (exists) {
            if (
              updatedProductPriceLevel.sellPrice &&
              Number(updatedProductPriceLevel.sellPrice) !== 0 &&
              !isNaN(Number(updatedProductPriceLevel.sellPrice))
            ) {
              await this.core.services.productPriceLevel.update(updatedProductPriceLevel, { id: null })
            }
          } else {
            await this.core.services.productPriceLevel.create(updatedProductPriceLevel, { id: null })
          }
        }
      }
      this.productDetailService.product = await this.core.services.product
        .update(this.productDetailService.product, SchemaUtil.product)
        .then(result => {
          this.notificationService.success(this.translateService.instant('alert.product.saved'))
          return result
        })
      if (this.productDetailService.parameters) {
        await this.productDetailService.updateParams()
      }
      this.pristineProduct = structuredClone(this.productDetailService.product)
    } catch (e) {
      console.error(e)
    } finally {
      this.productDetailService.loading = false
    }
  }

  public async deleteProduct(): Promise<void> {
    if (!confirm(this.translateService.instant('product.delete.confirmation'))) {
      return new Promise(r => r())
    }
    this.productDetailService.loading = true
    try {
      this.productDetailService.product = await this.core.services.product
        .delete(this.productDetailService.product)
        .then(result => {
          this.notificationService.success(this.translateService.instant('alert.product.deleted'))
          return result
        })
      this.pristineProduct = structuredClone(this.productDetailService.product)
    } catch (e) {
      console.error(e)
    }
    this.queryClient.invalidateQueries({ queryKey: ['products'] })
    this.router.navigate(['/products'], { replaceUrl: true })
    this.productDetailService.loading = false
  }

  private formatDateStringFromDateTime(dateTime: string): string[] | string {
    return !!dateTime && dateTime.length > 0 ? dateTime.split(' ')[0] : ''
  }

  private hasRoleForMoveProducts(): boolean {
    const rolesForMoveProducts = [
      'ROLE_SUPER_ADMIN',
      'ROLE_ADMIN',
      'ROLE_CERT_LIST',
      'ROLE_SHOP_LIST',
      'ROLE_PRODUCT_MOVE_CREATE',
    ]
    return this.core.services.user.hasOneOfTheRoles(rolesForMoveProducts)
  }
}
